import { useBodyScrollLock } from '@ps/web-shared-components';
import classNames from 'clsx';
import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { Form } from 'react-final-form';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { Loader, PKR_LOADERS, useHomeGamesWidget, useIsMobileDevice } from '@swf/poker';

import Error from '../Modal/Error/Error';

import AdvancedOptions from './AdvancedOptions/AdvancedOptions';
import CreateTournamentContext from './CreateTournamentContext/CreateTournamentContext';
import Buttons from './QuickCreate/Buttons/Buttons';
import MainStep from './QuickCreate/MainStep/MainStep';
import { TXTCLI_Create_Tournament } from './QuickCreateTournament.messages.json';
import { active, container, loader, recurrence, wrapper } from './QuickCreateTournament.scss';
import Recurrence from './Recurrence/Content/Content';
import useCreateClubTournament from './hooks/useCreateClubTournament';

const Section: React.FC<React.PropsWithChildren<{ className?: string; isOpen: boolean }>> = ({
  className = '',
  isOpen,
  children
}) => {
  const ref = useRef<HTMLDivElement>(null);

  useBodyScrollLock(isOpen, ref.current);

  return (
    <div
      ref={ref}
      className={classNames(container, className, {
        [active]: isOpen
      })}
    >
      {children}
    </div>
  );
};

const QuickCreateTournament: React.FC = () => {
  const { formatMessage } = useIntl();
  const {
    setState,
    state: { widgetId, clubId, isAdvancedOptionsOpen, isRecurrenceOpen, error }
  } = useContext(CreateTournamentContext);
  const { widgetDispatch, widgetSelect } = useHomeGamesWidget(widgetId);
  const { createClubTournament } = useCreateClubTournament(true);
  const data = useSelector(widgetSelect.tournamentStructures);
  const loadingState = useSelector(widgetSelect.loading);
  const loading = loadingState[PKR_LOADERS.tournamentStructures];

  useEffect(() => {
    widgetDispatch.fetchTournamentStructures({ clubId: Number(clubId) });
  }, [clubId, widgetDispatch]);

  const isMobileDevice = useIsMobileDevice(true);

  const handleErrorClose = useCallback(
    () => setState((prevState) => ({ ...prevState, error: undefined })),
    [setState]
  );

  useEffect(() => {
    if (isMobileDevice) {
      setState((prevState) => ({ ...prevState, title: formatMessage(TXTCLI_Create_Tournament) }));
    } else {
      setState((prevState) => ({ ...prevState, title: '' }));
    }
  }, [setState, isMobileDevice, formatMessage]);

  return (
    // TODO: Use Skeleton
    // TODO: Calculate initialValues in reducer once and pass them down in the <Form /> component
    <Loader loading={loading || !data} className={loader}>
      <Form
        onSubmit={createClubTournament}
        // initialValues={initialValues}
        render={() => (
          <>
            <div className={wrapper}>
              <Section isOpen={!isAdvancedOptionsOpen && !isRecurrenceOpen}>
                <MainStep />
              </Section>
              <Section isOpen={!!isAdvancedOptionsOpen && !isRecurrenceOpen}>
                <AdvancedOptions />
              </Section>
              <Section isOpen={!!isRecurrenceOpen} className={recurrence}>
                <Helmet>
                  <link
                    href="https://fonts.googleapis.com/css2?family=Roboto+Mono:wght@300;700&display=swap"
                    rel="stylesheet"
                  />
                </Helmet>
                <Recurrence quickFlow />
              </Section>
            </div>
            <Buttons />
            <Error onClose={handleErrorClose} error={error} />
          </>
        )}
      />
    </Loader>
  );
};

export default QuickCreateTournament;
